import { useEffect, useRef, useState, type FormEvent } from "react";
import type { NotificationPreferences } from "@sol-office/contracts";
import { api, errorMessage, patch } from "../api";
import { ErrorNotice, Field, Modal, Spinner } from "../ui";
import { signalNotificationChange } from "./shared";

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
export function InboxPreferences({ onClose }: { onClose: () => void }) {
  const [original, setOriginal] = useState<NotificationPreferences | null>(
    null,
  );
  const [draft, setDraft] = useState<NotificationPreferences | null>(null);
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(original);
  const close = () => {
    if (
      !busy &&
      (!dirty || confirm("Discard your notification preference changes?"))
    )
      onClose();
  };
  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    void api<{ preferences: NotificationPreferences }>(
      "/notification-preferences",
      { signal: controller.signal },
    )
      .then((r) => {
        if (!controller.signal.aborted) {
          setOriginal(r.preferences);
          setDraft(r.preferences);
        }
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(errorMessage(e));
      });
    return () => {
      alive.current = false;
      controller.abort();
    };
  }, []);
  useEffect(() => {
    if (!dirty) return;
    const href = location.href;
    const before = (event: Event) => {
      if (event.defaultPrevented) return;
      if (busy || !confirm("Discard your notification preference changes?"))
        event.preventDefault();
    };
    const unload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const back = (event: PopStateEvent) => {
      if (busy || !confirm("Discard your notification preference changes?")) {
        event.stopImmediatePropagation();
        history.pushState({}, "", href);
      }
    };
    window.addEventListener("sol:before-navigate", before);
    window.addEventListener("beforeunload", unload);
    window.addEventListener("popstate", back, true);
    return () => {
      window.removeEventListener("sol:before-navigate", before);
      window.removeEventListener("beforeunload", unload);
      window.removeEventListener("popstate", back, true);
    };
  }, [dirty, busy]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft || !original) return;
    setBusy(true);
    setError("");
    try {
      await patch("/notification-preferences", {
        expectedVersion: original.version,
        timeZone: draft.timeZone,
        dnd: draft.dnd,
        digest: draft.digest,
      });
      if (alive.current) {
        signalNotificationChange();
        onClose();
      }
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return (
    <Modal
      title="A little more focus"
      subtitle="Choose when updates can interrupt your day."
      onClose={close}
    >
      {error && <ErrorNotice message={error} />}
      {!draft ? (
        <Spinner label="Loading notification preferences…" />
      ) : (
        <form className="inbox-preferences" onSubmit={save}>
          <Field label="Time zone">
            <input
              required
              value={draft.timeZone}
              onChange={(e) => setDraft({ ...draft, timeZone: e.target.value })}
              placeholder="Asia/Bangkok"
              disabled={busy}
            />
          </Field>
          <fieldset disabled={busy}>
            <legend>Do not disturb</legend>
            <label className="inbox-check">
              <input
                type="checkbox"
                checked={draft.dnd.enabled}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    dnd: { ...draft.dnd, enabled: e.target.checked },
                  })
                }
              />{" "}
              Keep alerts quiet during these hours
            </label>
            <p className="inbox-help">
              Your inbox and unread count stay up to date. Alerts wait until
              quiet hours end.
            </p>
            <div className="inbox-time-pair">
              <Field label="Quiet hours start">
                <input
                  type="time"
                  required
                  value={draft.dnd.start}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      dnd: { ...draft.dnd, start: e.target.value },
                    })
                  }
                />
              </Field>
              <Field label="Quiet hours end">
                <input
                  type="time"
                  required
                  value={draft.dnd.end}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      dnd: { ...draft.dnd, end: e.target.value },
                    })
                  }
                />
              </Field>
            </div>
            <div className="inbox-weekdays">
              {days.map((day, i) => (
                <label key={day}>
                  <input
                    type="checkbox"
                    checked={draft.dnd.days.includes(i + 1)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        dnd: {
                          ...draft.dnd,
                          days: e.target.checked
                            ? [...draft.dnd.days, i + 1].sort()
                            : draft.dnd.days.filter((x) => x !== i + 1),
                        },
                      })
                    }
                  />
                  <span>{day.slice(0, 3)}</span>
                </label>
              ))}
            </div>
            <small className="inbox-help">
              For overnight hours, choose the day the quiet period begins.
            </small>
          </fieldset>
          <fieldset disabled={busy}>
            <legend>Your digest</legend>
            <Field label="Digest frequency">
              <select
                value={draft.digest.frequency}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    digest: {
                      ...draft.digest,
                      frequency: e.target
                        .value as NotificationPreferences["digest"]["frequency"],
                    },
                  })
                }
              >
                <option value="off">Off</option>
                <option value="daily">Every day</option>
                <option value="weekly">Every week</option>
              </select>
            </Field>
            {draft.digest.frequency !== "off" && (
              <>
                <div className="inbox-time-pair">
                  <Field label="Digest time">
                    <input
                      type="time"
                      required
                      value={draft.digest.time}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          digest: { ...draft.digest, time: e.target.value },
                        })
                      }
                    />
                  </Field>
                  {draft.digest.frequency === "weekly" && (
                    <Field label="Digest day">
                      <select
                        value={draft.digest.day}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            digest: {
                              ...draft.digest,
                              day: Number(e.target.value),
                            },
                          })
                        }
                      >
                        {days.map((day, i) => (
                          <option value={i + 1} key={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                </div>
                <label className="inbox-check">
                  <input
                    type="checkbox"
                    checked={draft.digest.channels.includes("in_app")}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        digest: {
                          ...draft.digest,
                          channels: e.target.checked
                            ? [
                                ...draft.digest.channels.filter(
                                  (c) => c !== "in_app",
                                ),
                                "in_app",
                              ]
                            : draft.digest.channels.filter(
                                (c) => c !== "in_app",
                              ),
                        },
                      })
                    }
                  />{" "}
                  Collect a digest in my inbox
                </label>
                <label className="inbox-check">
                  <input
                    type="checkbox"
                    checked={draft.digest.channels.includes("email")}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        digest: {
                          ...draft.digest,
                          channels: e.target.checked
                            ? [
                                ...draft.digest.channels.filter(
                                  (c) => c !== "email",
                                ),
                                "email",
                              ]
                            : draft.digest.channels.filter(
                                (c) => c !== "email",
                              ),
                        },
                      })
                    }
                  />{" "}
                  Email me a digest
                </label>
                {draft.emailDelivery !== "enabled" && (
                  <p className="inbox-help">
                    Email delivery is not set up yet. Your preference can be
                    saved; no email will be reported as sent.
                  </p>
                )}
              </>
            )}
          </fieldset>
          <div className="inbox-form-actions">
            <button
              className="button secondary"
              type="button"
              disabled={busy}
              onClick={close}
            >
              Cancel
            </button>
            <button className="button primary" disabled={busy || !dirty}>
              {busy ? "Saving…" : "Save preferences"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
